// src/components/AccountSavedPanel.jsx
//
// N1-b: Account "Notes & Saved" tab -- two read-only lists for the signed-in user:
//   - My Notes  (live /users/{uid}/notes via useAllNotes): pack + section + body preview.
//   - Saved     (live /users/{uid}/bookmarks via useBookmarks): pack + section.
// Each row deep-links back to that pack's section in the reader (?item=<itemId>); a
// note row also asks the reader to auto-open the notes drawer (&notes=1). Editing /
// deleting stays in the reader (this is a read-only overview). Newest first.
import React from 'react';
import NotesWorkspace from './NotesWorkspace.jsx';
import { useNavigate } from 'react-router-dom';
import { useAllNotes, useBookmarks } from '../storage/useUserContent.js';
import { getPackType } from '../utils/packHelpers.js';
import { IconBookmark, IconBookOpen } from './icons.jsx';

// Firestore Timestamp | Date | ms -> ms (missing sorts last).
function tsMillis(t) {
    if (!t) return 0;
    if (typeof t.toMillis === 'function') return t.toMillis();
    if (typeof t.seconds === 'number') return t.seconds * 1000;
    const n = Number(t);
    return Number.isFinite(n) ? n : 0;
}

function preview(s, n = 140) {
    const t = String(s == null ? '' : s).replace(/\s+/g, ' ').trim();
    return t.length > n ? t.slice(0, n) + '…' : t;
}

const ROUTE_BY_TYPE = {
    'set-text': '/set-text/',
    'skills-workshop': '/skills-workshop/',
    'literature-study': '/literature-study/',
};

export default function AccountSavedPanel({ state }) {
    const navigate = useNavigate();
    const { notes, loading: notesLoading } = useAllNotes();
    const { bookmarks, loading: bmLoading } = useBookmarks();
    const packs = (state && state.packs) || [];

    const packById = (id) => packs.find((p) => p.id === id) || null;
    const packLabel = (id) => { const p = packById(id); return (p && p.label) || id; };

    // Route by PACK TYPE (so skills packs go to /skills-workshop, not /literature-study);
    // fall back to itemType when the pack isn't in the loaded catalog.
    const deepLink = (row, openNotes) => {
        if (!row || !row.packId) return;
        const p = packById(row.packId);
        const base = p ? ROUTE_BY_TYPE[getPackType(p)] : null;
        const path = base
            ? base + row.packId
            : (row.itemType === 'set_text' ? '/set-text/' + row.packId : '/literature-study/' + row.packId);
        const q = new URLSearchParams();
        if (row.itemId) q.set('item', row.itemId);
        if (openNotes) q.set('notes', '1');
        const qs = q.toString();
        navigate(path + (qs ? '?' + qs : ''));
    };

    const sortedNotes = [...notes].sort((a, b) => tsMillis(b.updatedAt) - tsMillis(a.updatedAt));
    const sortedBookmarks = [...bookmarks].sort((a, b) => tsMillis(b.createdAt) - tsMillis(a.createdAt));

    return (
        <div className="space-y-10">
            {/* My Notes */}
            <div>
                <div className="flex items-center gap-3 mb-4">
                    <IconBookOpen className="w-4 h-4 text-midnight-accent" />
                    <h3 className="font-display text-midnight-text text-lg">My Notes</h3>
                    <div className="flex-grow h-px bg-midnight-accent/30" />
                </div>
                <NotesWorkspace notes={sortedNotes} loading={notesLoading} packLabel={packLabel} openSource={n => deepLink(n, true)} />
            </div>

            {/* Saved (bookmarks) */}
            <div>
                <div className="flex items-center gap-3 mb-4">
                    <IconBookmark className="w-4 h-4 text-midnight-accent" />
                    <h3 className="font-display text-midnight-text text-lg">Saved</h3>
                    <div className="flex-grow h-px bg-midnight-accent/30" />
                </div>
                {bmLoading ? (
                    <p className="font-serif italic text-midnight-muted text-sm">Loading…</p>
                ) : sortedBookmarks.length === 0 ? (
                    <p className="font-serif italic text-midnight-muted text-sm">No saved sections yet.</p>
                ) : (
                    <div className="grid sm:grid-cols-2 gap-2">
                        {sortedBookmarks.map((b) => (
                            <button key={b.id} onClick={() => deepLink(b, false)}
                                className="text-left border border-midnight-accent/20 bg-midnight-page/60 p-3 hover:border-midnight-accent/60 transition-colors text-midnight-accent flex items-start gap-2">
                                <IconBookmark className="w-4 h-4 mt-0.5 flex-shrink-0 opacity-70" />
                                <div className="min-w-0">
                                    <div className="font-display text-midnight-text text-sm truncate">{b.title || b.itemId}</div>
                                    <div className="font-serif italic text-midnight-muted text-xs truncate">{packLabel(b.packId)}</div>
                                </div>
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
